// @archigraph r8DljQiU
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { Container } from '../ui/Container';

export function Header() {
  const { isAuthenticated, user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-bg/80 backdrop-blur-md border-b border-border">
      <Container>
        <nav
          className="flex items-center justify-between h-14"
          aria-label="Main navigation"
        >
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 text-primary hover:text-primary-light transition-colors"
            aria-label="ArchiGraph home"
          >
            <svg
              className="h-6 w-6"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M8 24L16 8L24 24"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="square"
              />
              <rect x="14" y="6" width="4" height="4" fill="currentColor" />
              <rect x="6" y="22" width="4" height="4" fill="currentColor" />
              <rect x="22" y="22" width="4" height="4" fill="currentColor" />
            </svg>
            <span className="text-sm font-bold uppercase tracking-widest text-text">archigraph</span>
          </Link>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/docs"
              className="text-xs uppercase tracking-wider text-text-light hover:text-primary font-medium transition-colors px-3 py-2"
            >
              Docs
            </Link>
            <Link
              to="/download"
              className="text-xs uppercase tracking-wider text-text-light hover:text-primary font-medium transition-colors px-3 py-2"
            >
              Download
            </Link>

            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  onBlur={() => setTimeout(() => setUserMenuOpen(false), 150)}
                  className="flex items-center gap-2 text-text-light hover:text-text text-xs uppercase tracking-wider font-medium transition-colors px-3 py-2 min-h-[44px]"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  <span className="inline-flex items-center justify-center h-6 w-6 border border-primary text-primary text-xs font-bold">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                  <span className="hidden lg:inline">{user?.name || 'User'}</span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-1 w-48 bg-bg-white border border-border py-1 z-50">
                    <Link
                      to="/dashboard"
                      className="block px-4 py-2 text-xs uppercase tracking-wider text-text-light hover:text-primary hover:bg-bg transition-colors"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <hr className="my-1 border-border" />
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider text-error hover:bg-error-light transition-colors"
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-xs uppercase tracking-wider text-text-light hover:text-primary font-medium transition-colors px-3 py-2"
                >
                  Log in
                </Link>
                <Link
                  to="/signup"
                  className="text-xs uppercase tracking-wider text-bg bg-primary hover:bg-primary-light font-bold transition-colors px-4 py-2 ml-2"
                >
                  Sign up
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-text-light hover:text-primary min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-expanded={mobileMenuOpen}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? (
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border py-4 space-y-1">
            <Link
              to="/docs"
              className="block px-3 py-2 text-xs uppercase tracking-wider text-text-light hover:text-primary font-medium transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Docs
            </Link>
            <Link
              to="/download"
              className="block px-3 py-2 text-xs uppercase tracking-wider text-text-light hover:text-primary font-medium transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Download
            </Link>
            {isAuthenticated ? (
              <>
                <Link
                  to="/dashboard"
                  className="block px-3 py-2 text-xs uppercase tracking-wider text-text-light hover:text-primary font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Dashboard
                </Link>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="w-full text-left px-3 py-2 text-xs uppercase tracking-wider text-error hover:bg-error-light font-medium transition-colors"
                >
                  Log out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="block px-3 py-2 text-xs uppercase tracking-wider text-text-light hover:text-primary font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Log in
                </Link>
                <Link
                  to="/signup"
                  className="block px-3 py-2 text-xs uppercase tracking-wider text-primary font-bold transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign up
                </Link>
              </>
            )}
          </div>
        )}
      </Container>
    </header>
  );
}
